import React, { type FC } from "react";
import { IconButton, InputAdornment, TextField, type TextFieldProps } from "@mui/material";
import { useIntl } from "react-intl";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

export interface SearchTextFieldProps extends Omit<TextFieldProps, "onChange"> {
	readonly value: string;
	readonly onChange: (value: string) => void;
	readonly autoSelectOnFocus?: boolean;
}

export const SearchTextField: FC<SearchTextFieldProps> = ({
	value,
	onChange,
	onFocus,
	placeholder,
	autoSelectOnFocus = false,
	slotProps,
	...textFieldProps
}) => {
	const intl = useIntl();

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		onFocus?.(e);

		if (autoSelectOnFocus) {
			// Use setTimeout to avoid conflicts with touch events
			setTimeout(() => {
				e.target.select();
			}, 0);
		}
	};

	const handleClear = () => {
		onChange("");
	};

	return (
		<TextField
			{...textFieldProps}
			sx={{
				"& input:placeholder-shown": {
					textOverflow: "ellipsis",
				},
				borderRadius: 1,
				...textFieldProps?.sx,
			}}
			value={value}
			onChange={(e) => onChange(e.target.value)}
			onFocus={handleFocus}
			placeholder={
				placeholder ||
				intl.formatMessage({
					id: "search-text-field.placeholder",
					defaultMessage: "Search...",
				})
			}
			slotProps={{
				...slotProps,
				input: {
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								onClick={handleClear}
								edge="end"
								size="small"
								aria-label={intl.formatMessage({
									id: "search-text-field.clear",
									defaultMessage: "Clear search",
								})}
								sx={{
									visibility: value ? "visible" : "hidden",
								}}
							>
								<CloseIcon />
							</IconButton>
						</InputAdornment>
					),
					...slotProps?.input,
				},
			}}
		/>
	);
};
